import React, { useEffect, useMemo, useState } from "react";
import api from "../auth/api";

function cls(...a) {
  return a.filter(Boolean).join(" ");
}

function fmtDate(dateStr) {
  if (!dateStr) return "";
  try {
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return String(dateStr).slice(0, 10);
    return d.toLocaleDateString();
  } catch {
    return String(dateStr).slice(0, 10);
  }
}

function fmtTime(hora) {
  if (!hora) return "";
  const s = String(hora);
  const m = s.match(/^(\d{2}:\d{2}:\d{2})/);
  return m ? m[1] : s.slice(0, 8);
}

export default function SiniestralidadPage() {
  const [contrato, setContrato] = useState("");

  const [q, setQ] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [perfil, setPerfil] = useState("");
  const [telefono, setTelefono] = useState("");
  const [codigoTipoServicio, setCodigoTipoServicio] = useState("");
  const [order, setOrder] = useState("desc");

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [searched, setSearched] = useState(false);

  const canQuery = useMemo(() => !!String(contrato || "").trim(), [contrato]);

  const fetchData = async (opts = {}) => {
    const contratoValue = String(opts.contrato ?? contrato).trim();
    if (!contratoValue) {
      setErr("Debe ingresar un número de contrato.");
      setRows([]);
      setPagination(null);
      return;
    }

    setLoading(true);
    setErr("");

    const p = opts.page ?? page;
    const ps = opts.pageSize ?? pageSize;

    try {
      const { data } = await api.get(
        `/contratos/${encodeURIComponent(contratoValue)}/siniestralidad`,
        {
          params: {
            page: p,
            pageSize: ps,
            q: q || undefined,
            dateFrom: dateFrom || undefined,
            dateTo: dateTo || undefined,
            perfil: perfil || undefined,
            telefono: telefono || undefined,
            codigoTipoServicio: codigoTipoServicio || undefined,
            order: order || "desc",
          },
        }
      );

      setRows(data?.rows || []);
      setPagination(data?.pagination || null);
      setSearched(true);
    } catch (e) {
      setRows([]);
      setPagination(null);
      setErr(e?.response?.data?.error || e?.message || "Error consultando siniestralidad");
      setSearched(true);
    } finally {
      setLoading(false);
    }
  };

  const handleBuscar = () => {
    setPage(1);
    fetchData({ page: 1 });
  };

  const handleLimpiar = () => {
    setContrato("");
    setQ("");
    setDateFrom("");
    setDateTo("");
    setPerfil("");
    setTelefono("");
    setCodigoTipoServicio("");
    setOrder("desc");
    setPage(1);
    setPageSize(10);
    setRows([]);
    setPagination(null);
    setErr("");
    setSearched(false);
  };

  useEffect(() => {
    if (!searched || !canQuery) return;

    const t = setTimeout(() => {
      setPage(1);
      fetchData({ page: 1 });
    }, 250);

    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, dateFrom, dateTo, perfil, telefono, codigoTipoServicio, order, pageSize]);

  useEffect(() => {
    if (!searched || !canQuery) return;
    fetchData({ page });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const pages = pagination?.pages || 1;
  const total = pagination?.total || 0;

  const pageButtons = useMemo(() => {
    const out = [];
    const max = pages;
    const cur = page;
    const start = Math.max(1, cur - 3);
    const end = Math.min(max, cur + 3);

    if (start > 1) out.push(1);
    if (start > 2) out.push("…");
    for (let i = start; i <= end; i++) out.push(i);
    if (end < max - 1) out.push("…");
    if (end < max) out.push(max);

    return out;
  }, [page, pages]);

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-6">
      <div className="mx-auto max-w-7xl">
        <div className="rounded-3xl border border-slate-200 bg-white shadow-sm overflow-hidden">
          <div className="px-6 py-5 border-b bg-gradient-to-r from-slate-50 to-white">
            <h1 className="text-2xl font-extrabold text-slate-900">
              Consulta de Siniestralidad
            </h1>
            <p className="text-sm text-slate-600 mt-1">
              Ingrese el número de contrato para consultar registros de siniestralidad.
            </p>
          </div>

          <div className="p-6 border-b bg-white">
            <div className="grid grid-cols-12 gap-3 items-end">
              <div className="col-span-12 md:col-span-4">
                <label className="text-xs font-extrabold text-slate-700">
                  Número de contrato
                </label>
                <input
                  value={contrato}
                  onChange={(e) => setContrato(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleBuscar();
                  }}
                  placeholder="Ej: 123456"
                  className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-slate-900 outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-300"
                />
              </div>

              <div className="col-span-12 md:col-span-8 flex gap-2 flex-wrap">
                <button
                  type="button"
                  onClick={handleBuscar}
                  disabled={loading || !canQuery}
                  className="px-4 py-2 rounded-xl bg-blue-600 text-white font-bold hover:bg-blue-700 disabled:opacity-50"
                >
                  {loading ? "Consultando..." : "Buscar"}
                </button>

                <button
                  type="button"
                  onClick={handleLimpiar}
                  className="px-4 py-2 rounded-xl border border-slate-200 bg-white text-slate-900 font-bold hover:bg-slate-50"
                >
                  Limpiar
                </button>

                {searched && pagination ? (
                  <div className="self-center text-sm text-slate-600">
                    Contrato: <b className="text-slate-900">{contrato}</b> · Total:{" "}
                    <b className="text-slate-900">{total}</b>
                  </div>
                ) : null}
              </div>
            </div>
          </div>

          <div className="p-6 border-b bg-slate-50">
            <div className="grid grid-cols-12 gap-3">
              <div className="col-span-12 md:col-span-3">
                <label className="text-xs font-extrabold text-slate-700">Buscar</label>
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Perfil, descripción, código o teléfono..."
                  className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-slate-900 outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-300"
                  disabled={!searched}
                />
              </div>

              <div className="col-span-6 md:col-span-2">
                <label className="text-xs font-extrabold text-slate-700">Desde</label>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-slate-900 outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-300"
                  disabled={!searched}
                />
              </div>

              <div className="col-span-6 md:col-span-2">
                <label className="text-xs font-extrabold text-slate-700">Hasta</label>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-slate-900 outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-300"
                  disabled={!searched}
                />
              </div>

              <div className="col-span-6 md:col-span-2">
                <label className="text-xs font-extrabold text-slate-700">Perfil</label>
                <input
                  value={perfil}
                  onChange={(e) => setPerfil(e.target.value)}
                  placeholder="Ej: MEDICO"
                  className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-slate-900 outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-300"
                  disabled={!searched}
                />
              </div>

              <div className="col-span-6 md:col-span-2">
                <label className="text-xs font-extrabold text-slate-700">Teléfono</label>
                <input
                  value={telefono}
                  onChange={(e) => setTelefono(e.target.value)}
                  placeholder="0999999999"
                  className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-slate-900 outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-300"
                  disabled={!searched}
                />
              </div>

              <div className="col-span-6 md:col-span-1">
                <label className="text-xs font-extrabold text-slate-700">Código</label>
                <input
                  value={codigoTipoServicio}
                  onChange={(e) => setCodigoTipoServicio(e.target.value)}
                  placeholder="12"
                  className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-slate-900 outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-300"
                  disabled={!searched}
                />
              </div>

              <div className="col-span-6 md:col-span-1">
                <label className="text-xs font-extrabold text-slate-700">Orden</label>
                <select
                  value={order}
                  onChange={(e) => setOrder(e.target.value)}
                  className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-slate-900 outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-300"
                  disabled={!searched}
                >
                  <option value="desc">Reciente</option>
                  <option value="asc">Antiguo</option>
                </select>
              </div>

              <div className="col-span-6 md:col-span-1">
                <label className="text-xs font-extrabold text-slate-700">Tamaño</label>
                <select
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                  className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-slate-900 outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-300"
                  disabled={!searched}
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
              </div>
            </div>
          </div>

          <div className="p-6 bg-slate-50">
            {err ? (
              <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-red-700 font-semibold mb-4">
                {err}
              </div>
            ) : null}

            {!searched && !loading ? (
              <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center text-slate-500">
                Escriba un número de contrato y presione <b>Buscar</b>.
              </div>
            ) : (
              <>
                <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
                  <div className="grid grid-cols-14 gap-0 px-4 py-3 border-b bg-slate-50 text-xs font-extrabold text-slate-600">
                    <div className="col-span-2">Fecha</div>
                    <div className="col-span-2">Hora</div>
                    <div className="col-span-3">Perfil</div>
                    <div className="col-span-2">Teléfono</div>
                    <div className="col-span-2">Código</div>
                    <div className="col-span-3">Descripción</div>
                  </div>

                  <div className="max-h-[60vh] overflow-y-auto">
                    {loading ? (
                      <div className="p-6 text-slate-600">Cargando…</div>
                    ) : rows.length === 0 ? (
                      <div className="p-6 text-slate-500">
                        No hay registros de siniestralidad con esos filtros.
                      </div>
                    ) : (
                      rows.map((r, idx) => (
                        <div
                          key={`${idx}_${r.fecha}_${r.hora}_${r.codigoTipoServicio}_${r.telefono || ""}`}
                          className="grid grid-cols-14 gap-0 px-4 py-3 border-b last:border-b-0 hover:bg-slate-50"
                        >
                          <div className="col-span-2 text-sm text-slate-900 font-semibold">
                            {fmtDate(r.fecha)}
                          </div>

                          <div className="col-span-2 text-sm text-slate-700">
                            {fmtTime(r.hora)}
                          </div>

                          <div className="col-span-3 text-sm text-slate-800">
                            {r.perfil || "—"}
                          </div>

                          <div className="col-span-2 text-sm text-slate-700">
                            {r.telefono || "—"}
                          </div>

                          <div className="col-span-2">
                            <span
                              className={cls(
                                "inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-bold",
                                "border-slate-200 text-slate-700 bg-white"
                              )}
                            >
                              {r.codigoTipoServicio || "—"}
                            </span>
                          </div>

                          <div className="col-span-3 text-sm text-slate-800 whitespace-pre-wrap">
                            {r.descripcion || "—"}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
                  <div className="text-sm text-slate-600">
                    Página <b className="text-slate-900">{page}</b> de{" "}
                    <b className="text-slate-900">{pages}</b>
                  </div>

                  <div className="flex items-center gap-2 flex-wrap">
                    <button
                      className="px-3 py-1.5 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-slate-900 font-bold disabled:opacity-50"
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={loading || page <= 1}
                    >
                      ←
                    </button>

                    {pageButtons.map((b, i) =>
                      b === "…" ? (
                        <span key={`dots_${i}`} className="px-2 text-slate-500">
                          …
                        </span>
                      ) : (
                        <button
                          key={`p_${b}`}
                          className={cls(
                            "px-3 py-1.5 rounded-xl border font-extrabold",
                            b === page
                              ? "bg-blue-600 text-white border-blue-600"
                              : "bg-white text-slate-900 border-slate-200 hover:bg-slate-50"
                          )}
                          onClick={() => setPage(Number(b))}
                          disabled={loading}
                        >
                          {b}
                        </button>
                      )
                    )}

                    <button
                      className="px-3 py-1.5 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-slate-900 font-bold disabled:opacity-50"
                      onClick={() => setPage((p) => Math.min(pages, p + 1))}
                      disabled={loading || page >= pages}
                    >
                      →
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}